import { Helmet } from 'react-helmet-async';
import {
  DEFAULT_SEO_DESCRIPTION,
  SITE_NAME,
  SITE_URL
} from '../../config/siteContent';

const DEFAULT_TITLE = `${SITE_NAME} | Ropa Urbana y Deportiva en Uruguay`;
const DEFAULT_IMAGE = '/images/remlogo.png';

export const toAbsoluteUrl = (value = '/') => {
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith('/') ? value : `/${value}`;
  return `${SITE_URL}${path}`;
};

export const buildProductSchema = (product, pageUrl) => {
  if (!product?.name) return null;

  const rawImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images
    : (product.image ? [product.image] : []);
  const price = Number(product.basePrice ?? product.price);
  const stock = Number(product.stock);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    ...(product.description ? { description: product.description.replace(/<[^>]*>/g, ' ').trim() } : {}),
    ...(rawImages.length ? { image: rawImages.map(toAbsoluteUrl) } : {}),
    ...(product.sku || product._id ? { sku: product.sku || product._id } : {}),
    brand: { '@type': 'Brand', name: SITE_NAME }
  };

  if (Number.isFinite(price) && price >= 0 && Number.isFinite(stock)) {
    schema.offers = {
      '@type': 'Offer',
      url: pageUrl,
      priceCurrency: 'UYU',
      price,
      availability: stock > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: { '@type': 'Organization', name: SITE_NAME }
    };
  }

  return schema;
};

export const buildBreadcrumbSchema = (breadcrumbs = []) => {
  if (!breadcrumbs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: toAbsoluteUrl(crumb.url)
    }))
  };
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'ClothingStore',
  name: SITE_NAME,
  url: SITE_URL,
  logo: toAbsoluteUrl(DEFAULT_IMAGE),
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Ciudad de la Costa',
    addressRegion: 'Canelones',
    addressCountry: 'UY'
  },
  areaServed: { '@type': 'Country', name: 'Uruguay' }
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: 'es-UY'
};

const SEO = ({
  title,
  description = DEFAULT_SEO_DESCRIPTION,
  image,
  url = '/',
  type = 'website',
  product = null,
  breadcrumbs = [],
  faqItems = [],
  noindex = false,
  includeWebsiteSchema = url === '/'
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const fullUrl = toAbsoluteUrl(url);
  const fullImage = image ? toAbsoluteUrl(image) : toAbsoluteUrl(DEFAULT_IMAGE);
  const productSchema = buildProductSchema(product, fullUrl);
  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs);
  const faqSchema = faqItems.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer }
    }))
  } : null;

  return (
    <Helmet>
      <html lang="es-UY" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={fullUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_UY" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      {includeWebsiteSchema && <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>}
      {productSchema && <script type="application/ld+json">{JSON.stringify(productSchema)}</script>}
      {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
    </Helmet>
  );
};

export const NoIndex = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

export default SEO;
